import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { retry } from '../utils/async';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.error('Error accessing localStorage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.error('Error setting localStorage:', error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing from localStorage:', error);
        }
      }
    }
  },
  global: {
    headers: {
      'Cache-Control': 'no-store',
      'X-Client-Info': 'supabase-js/2.39.7',
      'X-Client-Info': 'supabase-js/2.39.7',
      'X-Client-Name': '07am-app'
    },
    fetch: async (url, options = {}) => {
      const MAX_RETRIES = 5;
      const INITIAL_DELAY = 500; // 500ms initial delay
      const TIMEOUT = 15000; // 15 seconds timeout
      const RETRY_CODES = [408, 429, 500, 502, 503, 504];
      const NETWORK_ERRORS = ['Failed to fetch', 'NetworkError', 'timeout', 'abort'];

      const fetchWithTimeout = async (url: string, options: any) => {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), TIMEOUT);

        try {
          // Add cache-busting query parameter
          const urlObj = new URL(url);
          urlObj.searchParams.append('_t', Date.now().toString());

          const response = await fetch(urlObj.toString(), {
            ...options,
            signal: controller.signal,
            headers: {
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-store',
              'Pragma': 'no-cache',
              ...options.headers
            }
          });
          
          // Throw for 5xx errors, network failures, or auth errors
          if (response.status >= 500 || response.status === 401 || response.status === 403) {
            throw new Error(`Server error: ${response.status}`);
          }
          return response;
        } finally {
          clearTimeout(id);
        }
      };

      return retry(
        () => fetchWithTimeout(url, options),
        MAX_RETRIES,
        INITIAL_DELAY,
        (error) => {
          // Retry on network errors, timeouts, 5xx errors, and specific status codes
          if (error instanceof TypeError && error.message === 'Failed to fetch') {
            return true;
          }
          if (error instanceof DOMException && error.name === 'AbortError') {
            return true;
          }
          if (error instanceof Error && error.message.includes('Server error:')) {
            const statusMatch = error.message.match(/Server error: (\d+)/);
            const statusCode = statusMatch ? parseInt(statusMatch[1]) : 0;
            return RETRY_CODES.includes(statusCode);
          }
          return false;
        }
      );
    }
  }
});